import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { ReactElement, useEffect, useState } from 'react';
import { Linking, Modal, Platform, Pressable, StyleProp, StyleSheet, View, ViewStyle, useWindowDimensions } from 'react-native';
import useTranslation from '../../hooks/translation-hook';
import Label, { LabelType } from './Label';
import useCustomTheme from '../../hooks/theme-hook';
import CustomMarkdown from './CustomMarkDown';
import ModalContainer from './ModalContainer';
import Popup from './Popup';
import Animated, { Easing, interpolate, runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useAppDispatch } from '../../hooks/store-hook';
import { lightBoxActions } from '../../store/lightBoxSlice';

export interface MoreInfoButtonProps {
  style?: StyleProp<ViewStyle>;
  children: string;
  title?: string;
  showTitle?: boolean;
  color?: string;
  size?: number;
  externalLink?: string;
  openURL?: string;
  icon?: ReactElement;
  suspendDrawerOnOpen?: boolean;
  useLightBoxOnNative?: boolean;
}

const MoreInfoButton = (props: MoreInfoButtonProps) => {
  const layout = useWindowDimensions();
  const t = useTranslation();
  const theme = useCustomTheme();
  const dispatch = useAppDispatch();

  const [title, setTitle] = useState(props.title);
  const [visible, setVisible] = useState(false);
  const progress = useSharedValue(0);
  const useNativeLightBox = Platform.OS !== 'web' && props.useLightBoxOnNative === true;

  useEffect(() => {
    setTitle(props.title);
  }, [props.title]);

  const handleOnConfirm = () => {
    handleClose();
  };

  const handleClose = () => {
    if (useNativeLightBox) {
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setContent(<></>));
      dispatch(lightBoxActions.setOnClose(undefined));
      dispatch(lightBoxActions.setKeepDrawerVisible(false));
      dispatch(lightBoxActions.setOverlayVisible(true));
      dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
      dispatch(lightBoxActions.setContentVerticalAlign('center'));
      dispatch(lightBoxActions.setContentHorizontalAlign('center'));
      dispatch(lightBoxActions.setContentTopOffset(0));
      dispatch(lightBoxActions.setContentBottomOffset(0));
      dispatch(lightBoxActions.setContentLeftOffset(0));
      return;
    }

    // Keep the modal mounted while animating out, then unmount.
    progress.value = withTiming(
      0,
      { duration: 140, easing: Easing.in(Easing.quad) },
      (finished) => {
        if (finished) runOnJS(setVisible)(false);
      },
    );
  };

  const handleOpenInfo = () => {
    if (props.externalLink) {
      Linking.openURL(props.externalLink);
      return;
    }

    if (useNativeLightBox) {
      dispatch(lightBoxActions.setOverlayVisible(true));
      dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
      dispatch(lightBoxActions.setKeepDrawerVisible(true));
      dispatch(lightBoxActions.setContentVerticalAlign('center'));
      dispatch(lightBoxActions.setContentHorizontalAlign('center'));
      dispatch(lightBoxActions.setContentTopOffset(0));
      dispatch(lightBoxActions.setContentBottomOffset(0));
      dispatch(lightBoxActions.setContentLeftOffset(0));
      dispatch(lightBoxActions.setContent(renderPopupContent()));
      dispatch(lightBoxActions.setVisibility(true));
      return;
    }

    progress.value = 0;
    setVisible(true);
  };

  useEffect(() => {
    if (!visible) return;
    progress.value = withTiming(1, { duration: 200, easing: Easing.out(Easing.quad) });
  }, [visible, progress]);

  const backdropStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(progress.value, [0, 1], [0, 1]),
    } as any;
  });

  const cardStyle = useAnimatedStyle(() => {
    const opacity = interpolate(progress.value, [0, 1], [0, 1]);
    const translateY = interpolate(progress.value, [0, 1], [10, 0]);
    const scale = interpolate(progress.value, [0, 1], [0.96, 1]);

    return {
      opacity,
      transform: [{ translateY }, { scale }],
    } as any;
  });

  const renderModalContent = () => (
    <View style={styles.popupContent}>
      <ModalContainer
        buttonText={t('got-it')}
        onConfirm={handleOnConfirm}
        onClose={() => {
          handleClose();
        }}
      >
        <>
          <CustomMarkdown>{props.children}</CustomMarkdown>
          {props.openURL && (
            <Pressable
              onPress={() => {
                Linking.openURL(t(props.openURL));
              }}
            >
              <Label
                numberOfLines={1}
                style={{
                  width: layout.width > 1200 ? 460 : '100%',
                  fontSize: 16,
                  textAlign: layout.width > 1200 ? 'left' : 'center',
                  color: theme.text.sea,
                }}
              >
                {t('learn-more')}
              </Label>
            </Pressable>
          )}
        </>
      </ModalContainer>
    </View>
  );

  const renderPopupContent = () =>
    useNativeLightBox ? (
      renderModalContent()
    ) : (
      <Popup suspendDrawer={props.suspendDrawerOnOpen} scrollable={false}>
        {renderModalContent()}
      </Popup>
    );

  return (
    <>
      <Pressable style={[{ flexDirection: 'row', alignItems: 'center' }, props.style]} onPress={handleOpenInfo}>
        {props.icon ? props.icon : <FontAwesomeIcon size={props.size ?? 22} icon={faCircleQuestion} color={props.color ?? theme.text.titleDark.toString()} />}
        {props.showTitle && (
          <Label style={{ fontSize: 16, marginLeft: 8 }} variant={LabelType.titleDarkSmall}>
            {title}
          </Label>
        )}
      </Pressable>
      {!useNativeLightBox && <Modal animationType="none" visible={visible} transparent={true} onRequestClose={handleClose}>
        <View style={styles.modalRoot}>
          {/* Backdrop */}
          <Pressable style={StyleSheet.absoluteFillObject} onPress={handleClose}>
            <Animated.View style={[StyleSheet.absoluteFillObject, { backgroundColor: theme.overlayColor }, backdropStyle]} />
          </Pressable>

          {/* Card */}
          <View style={styles.center} pointerEvents="box-none">
            <Animated.View style={[styles.cardWrap, cardStyle]}>
              {renderPopupContent()}
            </Animated.View>
          </View>
        </View>
      </Modal>}
    </>
  );
};

export default MoreInfoButton;

const styles = StyleSheet.create({
  modalRoot: {
    flex: 1,
  },
  center: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  cardWrap: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  popupContent: {
    flexShrink: 1,
    maxWidth: '100%',
  },
});
